import _ from 'lodash'
import { DefaultProps } from './DefaultProps'
import { CidPath, Concept, ConceptsByCid, XFLOW_CONCEPT_MODIFIERS, XFLOW_PERMITTED_DIRITEM_MODIFIERS, newConcept, testConceptXfAttributeConflict, updateConceptFromXf } from '../../concept/Concept'
import { XPathCalc } from '../XPathCalc'
import type { NodeList, Element, Document } from '@xmldom/xmldom'
import { getStringXmlAttributes, xmlNodeIsElement } from '@fusion-advantage/backcompat'
// import { Timer } from '../Timer'
import GenericTagProcessor from './GenericTagProcessor'
import ConceptTagProcessor from './ConceptTagProcessor'
import BooleanTagProcessor from './BooleanTagProcessor'
import ValueListTagProcessor from './ValueListTagProcessor'
import { xfValueRangeFromDirItem } from '../../concept/ConceptValueRange'
import { SerializableGeneralConcepts } from '../GeneralConcepts'
import { shallowEqual } from '../../shallowEqual'
import { materializedPathConcat, materializedPathConcatIfNotLast } from '../../materializedPath'
import { Directory, DirectoryItem, dirItemIsDx } from '../../directory/Directory'

export const ERR_CID_EXPECTED = 'cid expected but not specified'
export const ERR_SUBCONCEPTCID_EXPECTED = 'subconceptCid expected but not specified'
export const ERR_CONCEPT_NOT_DEFINED = (cid: string) => `Concept ${cid} is not yet defined`
export const ERR_SUBCONCEPT_NOT_DEFINED = (cid: string) => `Subconcept ${cid} is not yet defined`
export const ERR_CONCEPT_ALREADY_DEFINED = (cid: string) => `Concept ${cid} is already defined and cannot be modified`
export const ERR_SUBCONCEPT_ALREADY_DEFINED = (cid: string) => `Subconcept ${cid} is already defined and cannot be modified`
export const ERR_DIRITEM_NOT_FOUND = (dirItemIdStr: string) => `Directory item ${dirItemIdStr} not found`
export const ERR_DIRITEM_CANNOT_MODIFY = (dirItemIdStr: string) => `Directory item ${dirItemIdStr} cannot be modified`

const isImplyingConceptModification = (node: Element): boolean => {
	for(let name of XFLOW_CONCEPT_MODIFIERS) {
		if(node.hasAttribute(name)) {
			return true
		}
	}
	return false
}

const rejectConceptModification = (concept: Concept, node: Element): boolean => {
	for(let name of XFLOW_CONCEPT_MODIFIERS) {
		if(node.hasAttribute(name)) {
			if(
				concept.dirItemId !== null &&
				concept.xpaths.length === 0 &&
				_.includes(XFLOW_PERMITTED_DIRITEM_MODIFIERS, name)
			) {
				// directory items may be updated in some ways by xflow once
			} else {
				// check if the concept modifier specifies a value
				// which is in conflict with the current definition
				if(testConceptXfAttributeConflict(concept, name, node.getAttribute(name))) {
					return true
				}
			}
		}
	}
	return false
}

const copyDirItemAttrToConceptNode = (dirItem: DirectoryItem, node: Element): boolean => {
	if(node.hasAttribute('title')) {
		return false
	}
	node.setAttribute('title', dirItem.Prompt_Text)
	if(dirItemIsDx(dirItem)) {
		if(node.hasAttribute('isDx')) {
			return false
		}
		node.setAttribute('isDx', 'true')
	}
	if(dirItem.Required) {
		if(node.hasAttribute('isRequired')) {
			return false
		}
		node.setAttribute('isRequired', 'true')
	}
	if(dirItem.ICD10) {
		if(node.hasAttribute('icd10')) {
			return false
		}
		node.setAttribute('icd10', dirItem.ICD10)
	}
	if(dirItem.Range_Low || dirItem.Range_High || dirItem.Normal_Low || dirItem.Normal_High) {
		if(node.hasAttribute('valueRange')) {
			return false
		}
		node.setAttribute('valueRange', xfValueRangeFromDirItem(dirItem))
	}
	return true
}

const modifyConcept = (concept: Concept, node: Element) => {
	const attributes = getStringXmlAttributes(node)
	updateConceptFromXf(concept, attributes)
}

export const setNodeError = (node: Element, message: string, removeChildren?: boolean) => {
	node.setAttribute('error', message)
	if(removeChildren === true) {
		while(node.lastChild !== null) {
			node.removeChild(node.lastChild)
		}
	}
}

class L6ToL5Processor {
	doc: Document
	rootNode: Element
	directory: Directory
	concepts: ConceptsByCid

	constructor(
		doc: Document,
		rootNode: Element,
		directory: Directory,
	) {
		this.doc = doc
		this.rootNode = rootNode
		this.directory = directory
		this.concepts = this.defaultConcepts()
	}

	defaultConcepts(): ConceptsByCid {
		return {
			...SerializableGeneralConcepts,
		}
	}

	conceptExists(cid: PropertyKey): boolean {
		return this.concepts.hasOwnProperty(cid)
	}

	getConcept(node: Element, xpath: string, baseCidPath: CidPath): Concept|null {
		const cid = node.getAttribute('cid')
		if(cid === null) {
			setNodeError(node, ERR_CID_EXPECTED)
			return null
		}
		let concept: Concept|null = null
		if(this.conceptExists(cid)) {
			concept = this.concepts[cid]
		}
		if(isImplyingConceptModification(node)) {
			if(concept !== null) {
				if(rejectConceptModification(concept, node)) {
					setNodeError(node, ERR_CONCEPT_ALREADY_DEFINED(cid))
					return null
				}
			} else {
				concept = newConcept(cid)
				this.concepts[cid] = concept
			}
			modifyConcept(concept, node)
		}
		if(!this.conceptExists(cid)) {
			setNodeError(node, ERR_CONCEPT_NOT_DEFINED(cid))
			return null
		}
		concept!.xpaths.push(xpath)
		// don't append cid to itself...
		// baseCidPath can already contain the concept cid during this getConcept()
		// for example from inside <ValueListBooleans /> processing
		const cidpath = materializedPathConcatIfNotLast(baseCidPath, cid)
		// this here could technically still result in a very-edge-case
		// (but still legal) duplicate, how much do we care??
		if(concept!.cidpaths.at(-1) !== cidpath) {
			concept!.cidpaths.push(cidpath)
		}
		return this.concepts[cid]
	}

	getSubconcept(node: Element, xpath: string, baseCidPath: CidPath): [Concept|null, Concept|null] {
		const superCid = node.getAttribute('cid')
		if(superCid === null) {
			setNodeError(node, ERR_CID_EXPECTED)
			return [null, null]
		}
		const subconceptCid = node.getAttribute('subconceptCid')
		if(subconceptCid === null) {
			setNodeError(node, ERR_SUBCONCEPTCID_EXPECTED)
			return [null, null]
		}
		let subconcept: Concept|null = null
		if(this.conceptExists(subconceptCid)) {
			subconcept = this.concepts[subconceptCid]
		}
		if(isImplyingConceptModification(node)) {
			if(subconcept !== null) {
				if(rejectConceptModification(subconcept, node)) {
					setNodeError(node, ERR_SUBCONCEPT_ALREADY_DEFINED(subconceptCid))
					return [null, null]
				}
			} else {
				subconcept = newConcept(subconceptCid)
				this.concepts[subconceptCid] = subconcept
			}
			modifyConcept(subconcept, node)
		}
		if(!this.conceptExists(superCid)) {
			setNodeError(node, ERR_CONCEPT_NOT_DEFINED(superCid))
			return [null, null]
		}
		if(!this.conceptExists(subconceptCid)) {
			setNodeError(node, ERR_SUBCONCEPT_NOT_DEFINED(subconceptCid))
			return [null, null]
		}
		subconcept!.xpaths.push(xpath)
		subconcept!.cidpaths.push(materializedPathConcat(baseCidPath, subconceptCid))
		return [this.concepts[superCid], this.concepts[subconceptCid]]
	}

	translateDirItemId(node: Element, sourceAttr: string, destAttr: string, setAttributes: boolean): boolean {
		let dirItemIdStr = node.getAttribute(sourceAttr)
		if(dirItemIdStr !== null) {
			let dirItemId = parseInt(dirItemIdStr)
			let item = this.directory.getItem(dirItemId)
			if(!item) {
				setNodeError(node, ERR_DIRITEM_NOT_FOUND(dirItemIdStr), true)
				return false
			}
			if(setAttributes) {
				if(!copyDirItemAttrToConceptNode(item, node)) {
					setNodeError(node, ERR_DIRITEM_CANNOT_MODIFY(dirItemIdStr), true)
					return false
				}
			}
			node.setAttribute(destAttr, item.Cid)
		}
		return true
	}

	process() {
		// It's fast...
		// we don't need this logs clogging our tests right now
		// const timer = new Timer('[processL6ToL5]')
		if(!shallowEqual(this.concepts, this.defaultConcepts())) {
			throw new Error('[L6ToL5Processor] do not reuse instance')
		}
		this.traverse(this.rootNode.nodeName, '', this.rootNode.childNodes, { cid: null, subconceptCid: null })
		// timer.end()
	}

	traverse(baseXPath: string, baseCidPath: CidPath, childNodes: NodeList, defaultProps: DefaultProps) {
		const genericTagProcessor: GenericTagProcessor = new GenericTagProcessor(this)
		const conceptTagProcessor: ConceptTagProcessor = new ConceptTagProcessor(this)
		const booleanTagProcessor: BooleanTagProcessor = new BooleanTagProcessor(this)
		const valueListTagProcessor: ValueListTagProcessor = new ValueListTagProcessor(this)
		const xpathcalc = new XPathCalc(baseXPath)
		for(let nodeIdx in childNodes) {
			let node = childNodes[nodeIdx]
			if(!xmlNodeIsElement(node)) {
				continue
			}
			let xpath = xpathcalc.getNext(node.tagName)
			let concept: Concept|null = null
			// let subconcept: Concept|null = null
			let shouldTraverse: boolean = false
			switch(node.tagName) {
				case 'Concept':
					[shouldTraverse, concept] = conceptTagProcessor.process(node, xpath, baseCidPath, defaultProps)
					if(shouldTraverse) {
						this.traverse(
							xpath,
							materializedPathConcat(baseCidPath, concept!.cid),
							node.childNodes,
							{ cid: concept!.cid, subconceptCid: null },
						)
					}
					continue
				case 'Boolean':
					booleanTagProcessor.processBoolean(node, xpath, baseCidPath, defaultProps)
					continue
				case 'Check':
					booleanTagProcessor.processCheck(node, xpath, baseCidPath, defaultProps)
					continue
				case 'ValueListBooleans':
					valueListTagProcessor.processList(node, xpath, baseCidPath, defaultProps, "Boolean")
					continue
				case 'ValueListOptions':
					valueListTagProcessor.processList(node, xpath, baseCidPath, defaultProps, "Option")
					continue
				default:
					shouldTraverse = genericTagProcessor.process(node, defaultProps)
					if(shouldTraverse) {
						this.traverse(
							xpath,
							baseCidPath,
							node.childNodes,
							defaultProps
						)
					}
			}
		}
	}
}

export default L6ToL5Processor
